import Check from '../Check';
import Report from '../Report';
import { MessageType } from '../Message';
import type { IMethod } from '../../../stores/model/metamodel/method';

/**
 * Check the consistency of an acquired method.
 *
 * Checks that:
 * - the method has a viaDependency
 * - the method has a viaMethod
 * - the types of the method and its viaMethod are consistent
 * - the provenance is set to ACQUIRED
 */
class AcquiredMethodConsistencyCheck extends Check<IMethod> {
  static description = 'Check consistency of acquired method';

  check(): Report {
    this.report = new Report();

    // Check if there is a via dependency
    if (this.element.viaDependency === null) {
      this.report.addMessage(
        MessageType.ERROR,
        `Acquired method (${this.element.asString()}) has no originating dependency.`
      );
    } else {
      this.report.addMessage(
        MessageType.INFORMATION,
        `Acquired method (${this.element.asString()}) has originating dependency.`,
        this,
        this.element
      );
    }

    // Check if there is a via method
    if (this.element.viaMethod === null) {
      // Oops, there is no via method
      this.report.addMessage(
        MessageType.ERROR,
        `Acquired method (${this.element.asString()}) has no originating method.`,
        this,
        this.element
      );
    } else {
      // There is a via method
      this.report.addMessage(
        MessageType.INFORMATION,
        `Acquired method (${this.element.asString()}) has originating method.`,
        this,
        this.element
      );

      // Now check whether the type is consistent with the type of the via
      // method
      const viaType = this.element.viaMethod.type;
      const type = this.element.type;
      if (
        (viaType === 'CREATE' && type === 'END') ||
        (viaType === 'MODIFY' && type !== 'MODIFY') ||
        (viaType === 'END' && type === 'CREATE')
      ) {
        this.report.addMessage(
          MessageType.ERROR,
          "The acquired method type is not correct for the type of it's originating method.",
          this,
          this.element
        );
      } else {
        this.report.addMessage(
          MessageType.INFORMATION,
          'The acquired method has a valid type.',
          this,
          this.element
        );
      }
    }

    // Finally, check whether the provenance is set to ACQUIRED.
    if (this.element.provenance !== 'ACQUIRED') {
      this.report.addMessage(
        MessageType.ERROR,
        `Acquired method (${this.element.asString()}) does not have its provenance set to ACQUIRED.`,
        this,
        this.element
      );
    } else {
      this.report.addMessage(
        MessageType.INFORMATION,
        `Acquired method (${this.element.asString()}) provenance is set correctly.`,
        this,
        this.element
      );
    }

    return this.report;
  }
}

export default AcquiredMethodConsistencyCheck;
